import { useCallback } from 'react';

import {
  ClientPortalForms_Block,
  ClientPortalForms_BlockField,
  ClientPortalLogicTypeEnum,
  ClientPortalStepTypeEnum,
} from '@dotfile/shared/data-access-client-portal';
import {
  ClientPortalBlockTypeEnum,
  clientPortalCompanyPropertiesDefinition,
  generateKeyFromMapping,
  jsonLogic,
  jsonLogicVarCompatibility,
} from '@dotfile/shared/domain';

import {
  buildBlocksLogicDataContext,
  BuildBlocksLogicDataContextParams,
} from './build-blocks-logic-data-context';
import { LogicDataContext } from './build-step-logic-data-context';
import { mergeLogicDataContext } from './merge-logic-data-context';
import { useBuildStepLogicDataContext } from './use-build-step-logic-data-context';

type BlockLogicMatrixes = {
  display: Record<string, boolean>;
  required: Record<string, boolean>;
  readOnly: Record<string, boolean>;
};

export const useBlocksLogic = (blocks: ClientPortalForms_Block[]) => {
  const buildStepLogicDataContext = useBuildStepLogicDataContext();

  const computeBlockLogics = useCallback(
    (blockLogicParams: BuildBlocksLogicDataContextParams) => {
      const matrix = computeBlocksLogic(
        buildStepLogicDataContext(), // Recompute in the callback to make sure to have up-to-date values from the data-store
        buildBlocksLogicDataContext(blockLogicParams),
        blocks,
        blockLogicParams.stepType,
      );

      const displayedBlocks: (ClientPortalForms_Block & {
        isReadOnly: boolean;
      })[] = blocks
        .filter((block) => matrix.display[block.key])
        .map((field) => ({
          ...field,
          // This will be taken into account to display the required indicator and compute the validation schema
          isRequired: matrix.required[field.key],
          isReadOnly: matrix.readOnly[field.key],
        }));

      const displayedFields: (ClientPortalForms_BlockField & {
        isReadOnly: boolean;
      })[] = displayedBlocks.filter((blockField) =>
        isMetaBlockField(blockField),
      );

      const hiddenFields = blocks
        .filter((block) => !matrix.display[block.key])
        .filter(isBlockField);

      return {
        displayedBlocks,
        displayedFields,
        hiddenFields,
      };
    },
    [blocks, buildStepLogicDataContext],
  );

  return computeBlockLogics;
};

function computeBlocksLogic(
  stepLogicDataContext: LogicDataContext,
  blocksLogicDataContext: LogicDataContext,
  blocks: ClientPortalForms_Block[],
  stepType: ClientPortalStepTypeEnum,
): BlockLogicMatrixes {
  const matrix: BlockLogicMatrixes = {
    display: {},
    required: {},
    readOnly: {},
  };

  for (const block of blocks) {
    const logicDataContext = mergeLogicDataContext({
      stepLogicDataContext,
      blocksLogicDataContext,
      keepStepContextForKey: block.key,
    });

    // Display (all blocks)
    matrix.display[block.key] = computeMaybeLogic(
      block,
      ClientPortalLogicTypeEnum.display,
      logicDataContext,
      true,
      stepType,
    );

    // Required/Read only (field only)
    if (isBlockField(block)) {
      matrix.required[block.key] = computeMaybeLogic(
        block,
        ClientPortalLogicTypeEnum.required,
        logicDataContext,
        block.isRequired, // Default to isRequired settings instead of jsonLogic
        stepType,
      );
      matrix.readOnly[block.key] = computeMaybeLogic(
        block,
        ClientPortalLogicTypeEnum.read_only,
        logicDataContext,
        false, // not read only per default
        stepType,
      );
    }
  }

  return matrix;
}

function computeMaybeLogic(
  block: ClientPortalForms_Block,
  logicType: ClientPortalLogicTypeEnum,
  logicDataContext: LogicDataContext,
  defaultValue: boolean,
  stepType: ClientPortalStepTypeEnum,
) {
  // @TODO - 5149 - Client portal: Properly fix "never not required" Company status field
  // Remove this dirty hack
  if (
    block.key ===
      generateKeyFromMapping(
        clientPortalCompanyPropertiesDefinition.status.mapping,
      ) &&
    logicType === ClientPortalLogicTypeEnum.required
  ) {
    return true;
  }

  const logic = block.logics.find(
    (logic) => logic.type === logicType,
  )?.jsonLogic;

  if (logic) {
    const jsonLogicCompatible = jsonLogicVarCompatibility(logic, {
      type: stepType,
    });
    const result = jsonLogic.apply(jsonLogicCompatible, logicDataContext);
    return result;
  } else {
    return defaultValue;
  }
}

const isBlockField = (
  block: ClientPortalForms_Block,
): block is ClientPortalForms_BlockField =>
  block.type === ClientPortalBlockTypeEnum.field;

const isMetaBlockField = (
  block: ClientPortalForms_Block,
): block is ClientPortalForms_BlockField & {
  isReadOnly: boolean;
} => isBlockField(block) && 'isReadOnly' in block;
